/**
 * 21d review fix 1: the invitation token travels in the URL **fragment** (`/external#token=ext_…`), which browsers
 * never send to a server (not in the request line, not in `Referer`). It is read before any query runs, moved to this
 * tab's session storage, and removed from the address bar; the page also asks for no referrer at all.
 */
const STORAGE_KEY = 'wb.externalToken';
/** Fallback when session storage is unavailable (private mode): this page view only. */
let memoryToken: string | undefined;

/** Takes `token` from the location fragment (once), stores it and strips the fragment. */
export function takeTokenFromFragment(
  location: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
): string | undefined {
  const token = new URLSearchParams(location.hash.replace(/^#/, '')).get('token') ?? undefined;
  if (!token) return undefined;
  memoryToken = token;
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Private mode: the in-memory copy serves this page view.
  }
  window.history.replaceState(window.history.state, '', `${location.pathname}${location.search}`);
  return token;
}

/** The token of this tab, if any. */
export function externalToken(): string | undefined {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? memoryToken;
  } catch {
    return memoryToken;
  }
}

/** Forgets the token (revoked or expired invitation). */
export function forgetExternalToken(): void {
  memoryToken = undefined;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}

const META_ID = 'wb-external-referrer';

/** Adds `<meta name="referrer" content="no-referrer">` while the external page is shown; returns the remover. */
export function noReferrer(): () => void {
  if (!document.getElementById(META_ID)) {
    const meta = document.createElement('meta');
    meta.id = META_ID;
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.prepend(meta);
  }
  return () => document.getElementById(META_ID)?.remove();
}
